import React, { useEffect, useState } from "react";
import { subscribeToPush, registerSW } from "../push";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { Bell, Download } from "lucide-react";

const KEY = "tcm_install_dismissed_at";

export default function InstallPushBanner() {
  const { API, token, user } = useAuth();
  const [prompt, setPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showPush, setShowPush] = useState(false);

  useEffect(() => {
    registerSW();
    const dismiss = parseInt(localStorage.getItem(KEY) || "0");
    const visits = parseInt(localStorage.getItem("tcm_visits") || "0") + 1;
    localStorage.setItem("tcm_visits", String(visits));

    const beforeInstall = (e) => {
      e.preventDefault();
      setPrompt(e);
      if (visits >= 2 && Date.now() - dismiss > 7 * 24 * 3600 * 1000) setShowInstall(true);
    };
    window.addEventListener("beforeinstallprompt", beforeInstall);

    // iOS instruction case (no beforeinstallprompt on Safari)
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIOS && !isStandalone && visits >= 2 && Date.now() - dismiss > 7 * 24 * 3600 * 1000) {
      setShowInstall(true);
    }

    // Push prompt for lead clients on 2nd+ visit
    if (user?.role === "lead_client" && "Notification" in window && Notification.permission === "default"
        && visits >= 2 && Date.now() - dismiss > 7 * 24 * 3600 * 1000) {
      setShowPush(true);
    }
    return () => window.removeEventListener("beforeinstallprompt", beforeInstall);
  }, [user]);

  const install = async () => {
    if (prompt) { prompt.prompt(); await prompt.userChoice; setPrompt(null); }
    setShowInstall(false);
    localStorage.setItem(KEY, String(Date.now()));
  };

  const enablePush = async () => {
    const r = await subscribeToPush(API, token);
    if (r.ok) toast.success("Notifications on");
    else if (r.reason === "denied") toast.error("Notifications blocked in browser settings");
    else toast.error("Not supported on this device");
    setShowPush(false);
    localStorage.setItem(KEY, String(Date.now()));
  };

  const dismiss = () => {
    setShowInstall(false); setShowPush(false);
    localStorage.setItem(KEY, String(Date.now()));
  };

  if (!showInstall && !showPush) return null;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <div className="fixed top-4 left-4 right-4 z-50 max-w-md mx-auto" data-testid="install-push-banner">
      <div className="tcm-double-frame" style={{ background: "var(--tcm-cream)" }}>
        <div className="tcm-inner p-4 flex gap-3 items-start">
          <div className="flex-shrink-0 mt-1">
            {showInstall ? <Download size={18} /> : <Bell size={18} />}
          </div>
          <div className="flex-1">
            {showInstall && (
              <>
                <p className="font-display text-lg">Add to Home Screen</p>
                <p className="text-xs mt-1 text-[color:var(--tcm-navy-70)]">
                  {isIOS && !prompt
                    ? "Tap Share, then \"Add to Home Screen\" to keep your trip a tap away."
                    : "Install the portal for one-tap access during your trip."}
                </p>
              </>
            )}
            {showPush && !showInstall && (
              <>
                <p className="font-display text-lg">Live updates</p>
                <p className="text-xs mt-1 text-[color:var(--tcm-navy-70)]">
                  Get concierge messages the moment they land, not just by email.
                </p>
              </>
            )}
            <div className="flex gap-2 mt-3">
              {showInstall && prompt && (
                <button data-testid="install-btn" onClick={install} className="tcm-btn-primary text-[10px] py-2 px-3">Install</button>
              )}
              {showPush && (
                <button data-testid="push-btn" onClick={enablePush} className="tcm-btn-primary text-[10px] py-2 px-3">Turn on</button>
              )}
              <button data-testid="dismiss-banner" onClick={dismiss} className="tcm-btn-outline text-[10px] py-2 px-3">Later</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
